import { useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { hasPermission, ActionsEnum } from '@/lib/permissions';
import { AppShell } from './AppShell';
import { ShellHostCtx } from './shell-host-context';

/** Every page under this prefix renders inside the shell; nothing outside does. */
const SHELL_PREFIX = '/dashboard';

/**
 * Mounts the app shell ONCE, around the routes, instead of once per page.
 *
 * Each page used to render `<DashboardLayout>` itself, so every tap on a tab
 * tore the top bar, tab bar and sidebar down and built them again, and a page
 * whose code had not downloaded yet replaced the whole screen, tab bar
 * included, with the full-page loader. A phone app never does either.
 *
 * The decision is made from the URL and the session, before render, so the
 * shell is either there from the first frame of a page or not at all. The
 * conditions mirror ProtectedRoute: when any of them fails that guard
 * redirects, and the shell must not flash up for the one frame before it does.
 *
 * Inside the host, `DashboardLayout` and `Lazy` pass their children straight
 * through (see shell-host-context.ts).
 */
export function ShellHost({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { isAuthenticated, isLoading, clinic, user, permissions } = useAuth();
  const [fullHeight, setFullHeight] = useState(false);
  const value = useMemo(() => ({ setFullHeight }), []);

  const setupPending =
    clinic?.setupCompletedAt === null &&
    hasPermission(permissions, 'clinic', ActionsEnum.MANAGE);

  const active =
    pathname.startsWith(SHELL_PREFIX) &&
    !isLoading &&
    isAuthenticated &&
    !!clinic &&
    user?.isEmailVerified !== false &&
    user?.isActive !== false &&
    !setupPending;

  if (!active) return <>{children}</>;

  return (
    <ShellHostCtx.Provider value={value}>
      <AppShell fullHeight={fullHeight}>{children}</AppShell>
    </ShellHostCtx.Provider>
  );
}
